/**
 * MyPodOps Studio V2 - High-Quality Multi-Track Recorder
 *
 * Core recording engine supporting:
 * - 4K video capture (up to 3840x2160)
 * - 48kHz uncompressed audio
 * - Separate tracks per participant
 * - Progressive upload during recording
 * - Offline resilience (local storage fallback)
 */

import fixWebmDuration from 'webm-duration-fix';

// Recording quality presets
export const QUALITY_PRESETS = {
  '4K': { width: 3840, height: 2160, frameRate: 30 },
  '1080p': { width: 1920, height: 1080, frameRate: 30 },
  '720p': { width: 1280, height: 720, frameRate: 30 },
  'audio-only': { audio: true, video: false }
} as const;

export const AUDIO_CONFIG = {
  sampleRate: 48000,
  channelCount: 2,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true
};

// Supported codecs in order of preference
const VIDEO_CODECS = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=h264,opus',
  'video/webm'
];

const AUDIO_CODECS = [
  'audio/webm;codecs=opus',
  'audio/webm;codecs=pcm',
  'audio/webm'
];

export interface RecordingTrack {
  id: string;
  participantId: string;
  participantName: string;
  type: 'video' | 'audio' | 'screen';
  mimeType: string;
  chunks: Blob[];
  startTime: number;
  endTime?: number;
  dimensions?: { width: number; height: number };
  sampleRate?: number;
}

export interface RecorderOptions {
  quality: keyof typeof QUALITY_PRESETS;
  onDataAvailable?: (track: RecordingTrack, chunk: Blob) => void;
  onStop?: (track: RecordingTrack, blob: Blob) => void;
  onError?: (error: Error) => void;
  chunkInterval?: number; // ms between chunks for progressive upload
}

/**
 * Get the best supported MIME type for recording
 */
export function getSupportedMimeType(type: 'video' | 'audio'): string {
  const codecs = type === 'video' ? VIDEO_CODECS : AUDIO_CODECS;

  for (const codec of codecs) {
    if (MediaRecorder.isTypeSupported(codec)) {
      console.log(`[Recorder] Using codec: ${codec}`);
      return codec;
    }
  }

  // Fallback to browser default
  return type === 'video' ? 'video/webm' : 'audio/webm';
}

/**
 * High-quality media constraints for getUserMedia
 */
export function getMediaConstraints(
  quality: keyof typeof QUALITY_PRESETS,
  deviceIds?: { video?: string; audio?: string }
): MediaStreamConstraints {
  if (quality === 'audio-only') {
    return {
      video: false,
      audio: {
        deviceId: deviceIds?.audio ? { exact: deviceIds.audio } : undefined,
        ...AUDIO_CONFIG
      }
    };
  }

  const preset = QUALITY_PRESETS[quality] as { width: number; height: number; frameRate: number };

  return {
    video: {
      deviceId: deviceIds?.video ? { exact: deviceIds.video } : undefined,
      width: { ideal: preset.width, max: 3840 },
      height: { ideal: preset.height, max: 2160 },
      frameRate: { ideal: preset.frameRate, max: 60 }
    },
    audio: {
      deviceId: deviceIds?.audio ? { exact: deviceIds.audio } : undefined,
      ...AUDIO_CONFIG
    }
  };
}

/**
 * Screen capture constraints
 */
export function getScreenConstraints(): DisplayMediaStreamOptions {
  return {
    video: {
      displaySurface: 'monitor',
      width: { ideal: 3840, max: 3840 },
      height: { ideal: 2160, max: 2160 },
      frameRate: { ideal: 30, max: 60 }
    },
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false
    }
  };
}

/**
 * TrackRecorder - Records a single media track
 */
export class TrackRecorder {
  private recorder: MediaRecorder | null = null;
  private track: RecordingTrack;
  private options: RecorderOptions;
  private stream: MediaStream;

  constructor(
    stream: MediaStream,
    participantId: string,
    participantName: string,
    type: 'video' | 'audio' | 'screen',
    options: RecorderOptions
  ) {
    this.stream = stream;
    this.options = options;

    const mimeType = getSupportedMimeType(type === 'screen' ? 'video' : type);
    const videoTrack = stream.getVideoTracks()[0];
    const audioTrack = stream.getAudioTracks()[0];

    this.track = {
      id: `${participantId}-${type}-${Date.now()}`,
      participantId,
      participantName,
      type,
      mimeType,
      chunks: [],
      startTime: 0,
      dimensions: videoTrack ? {
        width: videoTrack.getSettings().width || 1920,
        height: videoTrack.getSettings().height || 1080
      } : undefined,
      sampleRate: audioTrack ? audioTrack.getSettings().sampleRate : undefined
    };
  }

  /**
   * Start recording with chunk-based data collection
   */
  start(): void {
    if (this.recorder) {
      console.warn('[TrackRecorder] Already recording');
      return;
    }

    try {
      // High bitrate for quality - will compress on server
      const videoBitsPerSecond = 8000000; // 8 Mbps for 4K
      const audioBitsPerSecond = 256000;  // 256 kbps for high-quality audio

      this.recorder = new MediaRecorder(this.stream, {
        mimeType: this.track.mimeType,
        videoBitsPerSecond,
        audioBitsPerSecond
      });

      this.recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          this.track.chunks.push(event.data);
          this.options.onDataAvailable?.(this.track, event.data);
        }
      };

      this.recorder.onerror = (event) => {
        console.error('[TrackRecorder] Error:', event);
        this.options.onError?.(new Error('Recording failed'));
      };

      this.recorder.onstop = async () => {
        await this.finalize();
      };

      this.track.startTime = Date.now();

      // Request data every N ms for progressive upload
      const interval = this.options.chunkInterval || 5000;
      this.recorder.start(interval);

      console.log(`[TrackRecorder] Started: ${this.track.id}`);
    } catch (error) {
      console.error('[TrackRecorder] Start failed:', error);
      this.options.onError?.(error as Error);
    }
  }

  /**
   * Stop recording and finalize the track
   */
  stop(): void {
    if (!this.recorder || this.recorder.state === 'inactive') {
      console.warn('[TrackRecorder] Not recording');
      return;
    }

    this.track.endTime = Date.now();
    this.recorder.stop();
  }

  /**
   * Pause recording
   */
  pause(): void {
    if (this.recorder?.state === 'recording') {
      this.recorder.pause();
      console.log(`[TrackRecorder] Paused: ${this.track.id}`);
    }
  }

  /**
   * Resume recording
   */
  resume(): void {
    if (this.recorder?.state === 'paused') {
      this.recorder.resume();
      console.log(`[TrackRecorder] Resumed: ${this.track.id}`);
    }
  }

  /**
   * Get recording state
   */
  get state(): RecordingState {
    return this.recorder?.state || 'inactive';
  }

  /**
   * Get track info
   */
  getTrack(): RecordingTrack {
    return this.track;
  }

  /**
   * Finalize the recording - combine chunks and fix duration
   */
  private async finalize(): Promise<void> {
    if (this.track.chunks.length === 0) {
      console.warn('[TrackRecorder] No chunks to finalize');
      return;
    }

    try {
      // Combine all chunks
      let blob = new Blob(this.track.chunks, { type: this.track.mimeType });

      // Fix WebM duration metadata issue
      if (this.track.mimeType.includes('webm')) {
        console.log('[TrackRecorder] Fixing WebM duration...');
        blob = await fixWebmDuration(blob);
      }

      console.log(`[TrackRecorder] Finalized: ${this.track.id}, size: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);

      this.options.onStop?.(this.track, blob);
    } catch (error) {
      console.error('[TrackRecorder] Finalize failed:', error);

      // Fallback: use raw blob without duration fix
      const blob = new Blob(this.track.chunks, { type: this.track.mimeType });
      this.options.onStop?.(this.track, blob);
    }
  }
}

/**
 * SessionRecorder - Manages multiple track recordings for a session
 */
export class SessionRecorder {
  private tracks: Map<string, TrackRecorder> = new Map();
  private options: RecorderOptions;

  constructor(_sessionId: string, options: RecorderOptions) {
    this.options = options;
  }

  /**
   * Add a track to record
   */
  addTrack(
    stream: MediaStream,
    participantId: string,
    participantName: string,
    type: 'video' | 'audio' | 'screen'
  ): string {
    const recorder = new TrackRecorder(stream, participantId, participantName, type, this.options);
    const trackId = recorder.getTrack().id;
    this.tracks.set(trackId, recorder);

    console.log(`[SessionRecorder] Added track: ${trackId}`);
    return trackId;
  }

  /**
   * Start recording all tracks
   */
  startAll(): void {
    console.log(`[SessionRecorder] Starting ${this.tracks.size} tracks`);
    this.tracks.forEach(recorder => recorder.start());
  }

  /**
   * Stop recording all tracks
   */
  stopAll(): void {
    console.log(`[SessionRecorder] Stopping ${this.tracks.size} tracks`);
    this.tracks.forEach(recorder => recorder.stop());
  }

  /**
   * Pause all tracks
   */
  pauseAll(): void {
    this.tracks.forEach(recorder => recorder.pause());
  }

  /**
   * Resume all tracks
   */
  resumeAll(): void {
    this.tracks.forEach(recorder => recorder.resume());
  }

  /**
   * Get all track info
   */
  getTracks(): RecordingTrack[] {
    return Array.from(this.tracks.values()).map(r => r.getTrack());
  }

  /**
   * Remove a track
   */
  removeTrack(trackId: string): void {
    const recorder = this.tracks.get(trackId);
    if (recorder) {
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
      this.tracks.delete(trackId);
    }
  }
}

/**
 * Mix multiple audio streams using Web Audio API
 */
export function mixAudioStreams(audioStreams: MediaStream[]): MediaStream {
  if (audioStreams.length === 0) {
    throw new Error('No audio streams to mix');
  }

  if (audioStreams.length === 1) {
    return audioStreams[0];
  }

  const audioContext = new AudioContext({ sampleRate: 48000 });
  const destination = audioContext.createMediaStreamDestination();

  audioStreams.forEach(stream => {
    const source = audioContext.createMediaStreamSource(stream);

    // Add a gain node for volume control
    const gainNode = audioContext.createGain();
    gainNode.gain.value = 1 / audioStreams.length; // Prevent clipping

    source.connect(gainNode);
    gainNode.connect(destination);
  });

  return destination.stream;
}

/**
 * Combine video and audio into a single stream
 */
export function combineStreams(videoStream: MediaStream, audioStream: MediaStream): MediaStream {
  return new MediaStream([
    ...videoStream.getVideoTracks(),
    ...audioStream.getAudioTracks()
  ]);
}
